import type { ReactNode } from 'react';
import { Box, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import { RiftboundCheckbox } from './ui/checkbox';

export interface FilterGroupProps {
  title: string;
  options: string[];
  selected: string[] | undefined;
  onToggle: (value: string) => void;
  collapsible?: boolean;
  /** UI label for each option value (filter values stay as raw option strings). */
  formatOptionLabel?: (value: string) => string;
  /** Optional mark rendered before the label (set logo thumbs). */
  renderLeading?: (value: string) => ReactNode;
}

/**
 * Checkbox checklist used by catalog sidebar and deck gallery filters
 * (same pattern as Champion / Set in SearchSidebar).
 */
export function FilterGroup({
  title,
  options,
  selected,
  onToggle,
  collapsible,
  formatOptionLabel = (v) => v,
  renderLeading,
}: FilterGroupProps) {
  if (!options.length) return null;
  const safeSelected = selected ?? [];
  const visible = collapsible && options.length > 12 ? options.slice(0, 12) : options;
  return (
    <Box>
      <Heading as="h3" size="sm" mb={2}>
        {title}
      </Heading>
      <Stack gap={1}>
        {visible.map((opt) => (
          <RiftboundCheckbox
            key={opt}
            size={{ base: 'md', md: 'sm' }}
            w="full"
            minH={{ base: '44px', md: 'auto' }}
            alignItems="center"
            checked={safeSelected.includes(opt)}
            onCheckedChange={() => onToggle(opt)}
            css={{
              '& [data-part="label"]': { flex: '1', minWidth: 0 },
            }}
          >
            {renderLeading ? (
              <HStack gap={2.5} align="center" minW={0} w="full">
                {renderLeading(opt)}
                <Box minW={0} lineHeight="short">
                  {formatOptionLabel(opt)}
                </Box>
              </HStack>
            ) : (
              formatOptionLabel(opt)
            )}
          </RiftboundCheckbox>
        ))}
        {collapsible && options.length > visible.length && (
          <Text fontSize="xs" color="textMuted">
            +{options.length - visible.length} more
          </Text>
        )}
      </Stack>
    </Box>
  );
}
